import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Box, Button, Group, Modal, Tabs, Text, createStyles} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import type {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import BioentitySearchView, {clearBioentitySearchViewSearchParams} from 'molbio/BioentitySearchView';
import type {ListableItem, ListableItemExtras} from 'search/SearchConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';
import APIUtil from 'util/APIUtil';

import {type FetchedEntityComponents, fetchEntityComponents} from './fetchEntityComponents';

const TEST_COMPONENT = 'EntitySearchModal';

// Where the picked entities land: each becomes its own input, or they all join one input.
export type EntitySearchDestination = 'inputs' | 'components';

// The ways to pick entities, one tab each.
export type EntitySearchTab = 'entities';

export type EntitySearchResult = FetchedEntityComponents;

interface EntitySearchModalProps {
  destination: EntitySearchDestination;
  initialTab: EntitySearchTab;
  // How many more entities the destination can take.
  maxEntities: number;
  moleculesSupported: boolean;
  onAdd: (result: EntitySearchResult) => void;
  onClose: () => void;
}

const COPY: Record<EntitySearchDestination, {title: string; unit: string}> = {
  inputs: {title: 'Add inputs', unit: 'input'},
  components: {title: 'Add components', unit: 'component'},
};

function isUnpredictableAaSequence(item: ListableItem): boolean {
  return (
    APIUtil.apiIdToType(item.id) === ModelTypeConstants.PROTEIN &&
    (item.permissions as ListableItemExtras['permissions'] & {structurePredictionEditable?: boolean})
      // Only an explicit grant allows prediction, matching the legacy Data tab.
      .structurePredictionEditable !== true
  );
}

function getInvalidItemTooltipText(item: ListableItem): string | undefined {
  return isUnpredictableAaSequence(item)
    ? "You don't have permission to run predictions on this AA sequence"
    : undefined;
}

/** Picks registry entities for New prediction and loads what each contributes as a component. */
export default function EntitySearchModal({
  destination,
  initialTab,
  maxEntities,
  moleculesSupported,
  onAdd,
  onClose,
}: EntitySearchModalProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [tab, setTab] = useState<EntitySearchTab>(initialTab);
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {title, unit} = COPY[destination];

  const availableTypes = useMemo(
    (): Array<OligoAwareFolderItemType> =>
      BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE && moleculesSupported
        ? [ModelTypeConstants.PROTEIN, ModelTypeConstants.MOLECULE]
        : [ModelTypeConstants.PROTEIN],
    [moleculesSupported]
  );

  // The search view keeps its query and filters in the store per nav section, so without this it
  // would reopen on whatever the last Model Hub search left behind, including other entity types.
  useEffect(() => {
    clearBioentitySearchViewSearchParams(availableTypes, Section.MODEL_HUB_STRUCTURE_PREDICTION);
  }, [availableTypes]);

  const selectedCount = selectedIds.length;
  const hasSelection = selectedCount > 0;
  const isOverLimit = selectedCount > maxEntities;

  // The picker can be dismissed while a load is in flight; its result then belongs to no picker, so
  // it's dropped rather than added.
  const isOpenRef = useRef(true);
  useEffect(() => {
    isOpenRef.current = true;
    return () => {
      isOpenRef.current = false;
    };
  }, []);

  const handleAddEntities = useCallback(
    (): Promise<void> =>
      fetchEntityComponents(selectedIds).then(
        (fetched) => {
          if (isOpenRef.current) {
            onAdd(fetched);
          }
        },
        () => {
          setError("The selected entities couldn't be loaded. Try again.");
        }
      ),
    [onAdd, selectedIds]
  );

  const handleAdd = (): void => {
    setIsAdding(true);
    setError(null);
    // Stays open on failure so the selection isn't lost.
    handleAddEntities().finally(() => {
      setIsAdding(false);
    });
  };

  return (
    // A px string: Mantine turns a numeric size into rem, which the monolith's 62.5% root font
    // size shrinks to ~60%. The search header needs ~870px to keep its filters on one row.
    <Modal onClose={onClose} opened={true} size="min(1200px, 90vw)" title={title}>
      <Tabs
        onTabChange={(value) => {
          setTab(value as EntitySearchTab);
          setError(null);
        }}
        value={tab}
      >
        <Tabs.List mb="md">
          <Tabs.Tab icon={<BenchlingIcon icon="search" />} value="entities">
            Entities
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="entities">
          <Box
            className={cx(classes.body, classes.searchView)}
            {...getTestProps({component: TEST_COMPONENT, element: 'root'})}
          >
            <BioentitySearchView
              availableTypes={availableTypes}
              getInvalidItemTooltipText={getInvalidItemTooltipText}
              isItemInvalid={isUnpredictableAaSequence}
              limitSelectedEntitiesParams={{
                maxNumEntitiesToAdd: maxEntities,
                selectedEntityName: pluralize(maxEntities, unit),
              }}
              navSection={Section.MODEL_HUB_STRUCTURE_PREDICTION}
              rowSelectionMode={RowSelectionMode.MULTIPLE}
              selectedBioentityIds={selectedIds}
              // The load in flight uses the selection from when Add was clicked, so it stays fixed until then.
              setSelectedBioentityIds={(ids: Array<string>) => {
                if (!isAdding) {
                  setSelectedIds(ids);
                }
              }}
              showInvalid={true}
            />
          </Box>
        </Tabs.Panel>
      </Tabs>
      {error != null && (
        <Alert icon={<BenchlingIcon icon="exclamation-circle" />} mt="md" variant="error">
          {error}
        </Alert>
      )}
      <Group mt="md" noWrap={true} position="apart">
        <Text color="dimmed" size="sm">
          {hasSelection
            ? `${selectedCount} ${pluralize(selectedCount, unit)} will be added`
            : 'Nothing selected yet'}
        </Text>
        <Group noWrap={true} spacing="sm">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button disabled={!hasSelection || isOverLimit} loading={isAdding} onClick={handleAdd}>
            {hasSelection ? `Add ${selectedCount}` : 'Add'}
          </Button>
        </Group>
      </Group>
    </Modal>
  );
}

// Matches the theme's Modal body padding (nucleus-ui Modal.styles.ts).
const MODAL_BODY_PADDING = 16;

const useStyles = createStyles(() => ({
  // Each tab fills the same bounded height and scrolls its own results, so switching tabs doesn't
  // resize the modal; molecule rows are ~200px tall, so leave room for several.
  body: {
    display: 'flex',
    flexDirection: 'column',
    height: 'min(640px, calc(100vh - 280px))',
    minHeight: 0,
  },
  // The listing already insets its header, so cancel the modal body's padding around it.
  searchView: {
    marginLeft: -MODAL_BODY_PADDING,
    marginRight: -MODAL_BODY_PADDING,
  },
}));
